/**
 * Dynamic dashboard — KPI tiles auto-generated from the user's visible
 * vector layers. Renders into the `#dashboardLive` slot in the right panel.
 *
 * The right-panel "analytics result" cards (analyticsResults.js) keep
 * landing above this in `#analyticsResults`.
 */
import { sampleRamp, pickCategoricalColor } from './colorRamps.js';

let userLayersRef = null;
let currentLayers = [];   // [{id, name, geometryType, features, category}]

export function initDashboard(manager) {
  userLayersRef = manager;
  refreshDashboard();
}

/**
 * Should be called whenever the visible-layer set changes (toggle on/off,
 * upload completes, project restored). Cheap; just re-renders.
 */
export function refreshDashboard() {
  if (!userLayersRef) return;
  currentLayers = userLayersRef.getVisibleVectorLayersWithFeatures?.() ?? [];

  const host = document.getElementById('dashboardLive');
  if (!host) return;

  if (currentLayers.length === 0) {
    host.innerHTML = `
      <div style="padding:36px 20px;text-align:center;color:var(--text3);font-family:var(--font-display);font-style:italic;font-size:13px;line-height:1.6">
        Upload a layer or pick one from the catalog to see live KPIs here.
      </div>
    `;
    return;
  }

  host.innerHTML = renderDynamicContent(currentLayers);
}

function renderDynamicContent(layers) {
  const totalFeatures = layers.reduce((s, l) => s + (l.features?.length || 0), 0);
  const numLayers = layers.length;

  // Header KPIs — always two: # layers visible, # features total
  const header = `
    <div class="sgrid">
      <div class="scard">
        <div class="sl">Layers visible</div>
        <div class="sv" style="color:var(--primary)">${numLayers.toLocaleString()}</div>
        <div class="sc" style="color:var(--text3)">across the map</div>
      </div>
      <div class="scard">
        <div class="sl">Total features</div>
        <div class="sv" style="color:var(--success)">${totalFeatures.toLocaleString()}</div>
        <div class="sc" style="color:var(--text3)">in your data</div>
      </div>
    </div>
  `;

  // Per-layer breakdown card — features by category if available, else just geometry types
  const layerCards = layers.map(renderLayerCard).join('');

  return header + layerCards;
}

function renderLayerCard(layer) {
  const features = layer.features || [];
  const total = features.length;

  // Pick the most populated string-typed property to break down by
  const breakdown = topCategoricalBreakdown(features);
  const subtitle = layer.geometryType
    ? `${escape(layer.geometryType)} · ${total.toLocaleString()} feature${total === 1 ? '' : 's'}`
    : `${total.toLocaleString()} feature${total === 1 ? '' : 's'}`;

  let body;
  if (breakdown && breakdown.entries.length > 1) {
    const max = Math.max(...breakdown.entries.map(e => e[1]));
    const ramp = sampleRamp('blue', breakdown.entries.length);
    body = `
      <div class="dash-bars">
        ${breakdown.entries.map(([k, v], i) => {
          const w = Math.max(2, (v / max) * 100);
          return `
            <div class="dash-bar-row">
              <div class="dash-bar-label">${escape(String(k))}</div>
              <div class="dash-bar-track">
                <div class="dash-bar-fill" style="width:${w}%;background:${ramp[i] || pickCategoricalColor(i)}"></div>
              </div>
              <div class="dash-bar-value">${v.toLocaleString()}</div>
            </div>
          `;
        }).join('')}
      </div>
      <div class="dash-bar-foot">by ${escape(breakdown.column)}</div>
    `;
  } else {
    // Fallback — just show feature count + bbox extent
    body = `
      <div class="dash-no-breakdown">
        ${escape(subtitle)}
      </div>
    `;
  }

  return `
    <div class="dash-layer-card">
      <header class="dash-layer-head">
        <div>
          <div class="dash-layer-name">${escape(layer.name)}</div>
          <div class="dash-layer-sub">${escape(subtitle)}</div>
        </div>
      </header>
      <div class="dash-layer-body">${body}</div>
    </div>
  `;
}

/**
 * Find the most-populated string-valued column in the feature set and
 * return a value→count map, sorted descending. Skips columns that look
 * like ids (every value distinct).
 */
function topCategoricalBreakdown(features, limit = 6) {
  if (!features.length) return null;
  const sample = features[0]?.properties || {};
  const candidates = Object.keys(sample).filter(k =>
    typeof sample[k] === 'string' && k.toLowerCase() !== 'name' && k !== 'geometry'
  );
  if (!candidates.length) return null;

  let best = null;
  for (const col of candidates) {
    const counts = new Map();
    for (const f of features) {
      const v = f.properties?.[col];
      if (v == null || v === '') continue;
      counts.set(v, (counts.get(v) || 0) + 1);
    }
    if (counts.size < 2) continue;
    if (counts.size > features.length * 0.8) continue;  // looks like an id
    if (!best || counts.size < best.size) {
      best = { column: col, counts, size: counts.size };
    }
  }
  if (!best) return null;
  const entries = [...best.counts.entries()].sort((a, b) => b[1] - a[1]).slice(0, limit);
  return { column: best.column, entries };
}

function escape(s) {
  return String(s ?? '').replace(/[&<>"]/g, c => ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;' }[c]));
}
